import { useEffect, useRef, useState } from "react";
import { useQueryClient } from "react-query";
import { useHistory } from "react-router-dom";
import { t } from "@/i18n-js/instance";
import { classNames } from "@/react/helpers/twMergeWithCN";
import { useWebSocket } from "@circle-react/hooks/useWebSocket";
import {
  ATTACHMENT_STATUS_COMPLETED,
  ATTACHMENT_STATUS_UPLOADING,
} from "../constants";
import { useHelpWidgetContext } from "../context/HelpWidgetContext";
import { useAttachmentsUpload } from "../hooks/useAttachmentsUpload";
import { useClickButton } from "../hooks/useClickButton";
import { useCreateChatMessage } from "../hooks/useCreateChatMessage";
import { useMarkAsRead } from "../hooks/useMarkAsRead";
import { useShowConversation } from "../hooks/useShowConversation";
import type { ChatCardProps } from "../types/ChatCardProps";
import type { ConversationChannel } from "../types/ConversationChannel";
import type { ConversationStatus } from "../types/ConversationStatus";
import type { HelpWidgetConversation } from "../types/help_widget_conversation";
import type { HelpWidgetMessage } from "../types/help_widget_message";
import { autolink } from "../utils/autolinkUtils";
import { appendMessageWithFadingEffect } from "../utils/messageHelpers";
import { ConversationClosedCta } from "./ConversationClosedCta";
import { ConversationViewSkeletonLoader } from "./ConversationViewSkeletonLoader";
import { LeaveQueueConfirmation } from "./LeaveQueueConfirmation";
import { LiveChatConversationClosedCta } from "./LiveChatConversationClosedCta";
import { QueueBanner } from "./QueueBanner";
import { ChatDivider } from "./common/ChatDivider";
import { ChatMessage } from "./common/ChatMessage";
import { CircleBotFooterMessage } from "./common/CircleBotFooterMessage";
import { HelpInputArea } from "./common/HelpInputArea";
import { Thinking } from "./common/Thinking";
import { FormAttachment } from "./forms/FormAttachment";

export const ConversationView = ({
  conversationId,
}: {
  conversationId: string;
}) => {
  const { isExpanded, isShowingLeaveWarning } = useHelpWidgetContext();
  const { data: conversation, isLoading } = useShowConversation(conversationId);
  const [status, setStatus] = useState<ConversationStatus | null>(null);
  const [channel, setChannel] = useState<ConversationChannel | null>(null);
  const [messages, setMessages] = useState<HelpWidgetMessage[]>([]);
  const [isBotProcessing, setIsBotProcessing] = useState(false);

  // Queue-related state
  const [isWaitingInQueue, setIsWaitingInQueue] = useState(false);
  const [queuePosition, setQueuePosition] = useState<number | null>(null);

  const messageBottomRef = useRef<HTMLDivElement>(null);
  const history = useHistory();
  const { markAsRead } = useMarkAsRead();

  useEffect(() => {
    if (conversation) {
      setStatus(conversation.status);
      setChannel(conversation.channel || null);
      setMessages(conversation.messages);
      setIsBotProcessing(conversation.is_bot_processing || false);
      setIsWaitingInQueue(conversation.is_waiting_in_queue || false);
      setQueuePosition(conversation.queue_position || null);
    }
  }, [conversation]);

  useEffect(() => {
    markAsRead({ conversationId });
  }, []);

  const scrollToBottom = () => messageBottomRef.current?.scrollIntoView();

  useWebSocket(
    {
      channel: "HelpWidget::ConversationChannel",
      onMessageReceive: (data: {
        event: string;
        message?: HelpWidgetMessage;
        conversation?: HelpWidgetConversation;
        url?: string;
        position?: number;
        agent_first_name?: string;
      }) => {
        if (data.event === "newMessage" && data.message) {
          const msg: HelpWidgetMessage = data.message;
          setMessages(prevMessages =>
            appendMessageWithFadingEffect(prevMessages, msg),
          );
          markAsRead({ conversationId });
        } else if (data.event === "redirectTo" && data.url) {
          history.push(data.url);
        } else if (data.event === "enterQueue") {
          scrollToBottom();
        } else if (data.event === "updateQueue") {
          setQueuePosition(data.position || 0);
        } else if (data.event === "updateConversation" && data.conversation) {
          setChannel(data.conversation.channel || null);
          setStatus(data.conversation.status || null);
          setIsBotProcessing(data.conversation.is_bot_processing || false);
          setIsWaitingInQueue(data.conversation.is_waiting_in_queue || false);
          setQueuePosition(data.conversation.queue_position || null);
        }
      },
      conversation_id: conversationId,
      canCreateConnection: Boolean(conversationId),
    },
    [conversationId],
  );

  const queryClient = useQueryClient();
  const { uploadAttachments, attachments, cancelAttachment, setAttachments } =
    useAttachmentsUpload({ conversationId });

  useEffect(() => {
    scrollToBottom();
  }, [messages, isBotProcessing, attachments]);

  const { mutate: createChatMessage } = useCreateChatMessage({
    onSuccess: (message: HelpWidgetMessage) => {
      if (channel === "circle_bot") {
        setIsBotProcessing(true);
      }
      setAttachments([]);
      void queryClient.invalidateQueries(["attachments", conversationId]);
      setMessages(prevMessages => [...prevMessages, message]);
    },
    onError: () => {},
  });

  const { mutate: clickButton } = useClickButton({
    onSuccess: () => {},
    onError: () => {},
  });

  const onActionButtonClick = (message: HelpWidgetMessage, event: string) => {
    clickButton({ conversationId, event, messageId: message.id });
  };

  const onActionCardClick = (externalURL: string) =>
    window.open(externalURL, "_blank");

  const extractButtons = (message: HelpWidgetMessage) => {
    if (message.buttons) {
      return message.buttons.map(button => ({
        ...button,
        onClick: () => onActionButtonClick(message, button.event),
        disabled: status === "closed",
      }));
    }
    return [];
  };

  const extractCards = (message: HelpWidgetMessage) => {
    if (message.cards) {
      return message.cards.map((card: ChatCardProps) => ({
        ...card,
        onClick: () => onActionCardClick(card.url),
      }));
    }
    return [];
  };

  const onSendMessage = (text: string) => {
    const attachmentIds = attachments.map(
      attachment => attachment.attachmentId,
    ) as string[];

    createChatMessage({
      conversationId,
      text,
      attachment_ids: attachmentIds,
    });
  };

  if (isLoading) {
    return (
      <div className="flex h-full w-full flex-1 flex-col">
        <ConversationViewSkeletonLoader />
      </div>
    );
  }

  if (isShowingLeaveWarning) {
    return <LeaveQueueConfirmation conversationId={conversationId} />;
  }

  const lastMessage = messages[messages.length - 1];
  const isExpectingButtonClick = lastMessage?.buttons?.length > 0;

  return (
    <div
      className={classNames("flex flex-1 flex-col", {
        "w-[736px]": isExpanded,
        "w-[400px]": !isExpanded,
      })}
    >
      <div className="mt-4 flex flex-1 flex-col justify-end">
        {messages?.map((message: HelpWidgetMessage, index: number) => {
          const previousMessage = index > 0 ? messages[index - 1] : null;
          const shouldShowAvatar =
            !previousMessage ||
            previousMessage.author_type !== message.author_type;

          return (
            <div
              key={message.id}
              className={classNames({
                "mt-5": shouldShowAvatar,
                "mt-2": !shouldShowAvatar,
              })}
            >
              {message.author_type === "system" ? (
                <ChatDivider message={message} />
              ) : (
                <ChatMessage
                  key={message.id}
                  authorName={message.author_name}
                  content={autolink(message.content)}
                  channel={message.channel}
                  createdAt={message.created_at.toLocaleString()}
                  type={
                    message.author_type === "agent"
                      ? "business"
                      : message.author_type
                  }
                  buttons={extractButtons(message)}
                  cards={extractCards(message)}
                  selectedButton={message.selected_button}
                  shouldShowAvatar={shouldShowAvatar}
                  showDisclaimer={false}
                  attachments={message.attachments}
                />
              )}
            </div>
          );
        })}
      </div>
      {(isBotProcessing || (channel === "live_chat" && isWaitingInQueue)) && (
        <div className="mt-4 flex flex-col">
          {isBotProcessing && <Thinking />}
          {channel === "live_chat" &&
            isWaitingInQueue &&
            queuePosition !== null && (
              <QueueBanner queuePosition={queuePosition} />
            )}
        </div>
      )}
      {status !== "closed" && (
        <>
          <div className="my-4 flex flex-col gap-2 px-4">
            {attachments.map(attachment => (
              <FormAttachment
                key={attachment.url}
                attachment={{
                  ...attachment,
                  status: attachment.completed
                    ? ATTACHMENT_STATUS_COMPLETED
                    : ATTACHMENT_STATUS_UPLOADING,
                }}
                onCancel={cancelAttachment}
              />
            ))}
          </div>
          <div className="bg-primary sticky bottom-0 z-10 mx-4 pb-4 pt-[2px]">
            <HelpInputArea
              onSubmit={onSendMessage}
              placeholder={t(
                "help_widget.conversation_page.help_input_placeholder",
              )}
              allowAttachments={channel !== "circle_bot"}
              uploadAttachments={uploadAttachments}
              attachments={attachments}
              isSubmitDisabled={isBotProcessing || isExpectingButtonClick}
            />
          </div>
          {channel === "circle_bot" && <CircleBotFooterMessage />}
        </>
      )}

      {status === "closed" &&
        (channel === "circle_bot" || channel === "email") && (
          <div className="bg-primary sticky bottom-0 z-10 rounded-b-2xl p-4">
            <ConversationClosedCta />
          </div>
        )}

      {status === "closed" && channel === "live_chat" && (
        <div className="bg-primary sticky bottom-0 z-10 rounded-b-2xl p-4">
          <LiveChatConversationClosedCta />
        </div>
      )}

      <div id="messages-end" ref={messageBottomRef} />
    </div>
  );
};
